import { useReducer, useEffect, useCallback } from 'react'
import HeaderBox from '../../../../components/generic/arpariBoxes/headerBox'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Unstable_Grid2'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import ListItemText from '@mui/material/ListItemText'
import CheckIcon from '@mui/icons-material/Check'
import theme from '../../../../helpers/theme'
import { useParams } from 'react-router-dom'
import { groupingTypeFilter } from '../constants'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import { backendGetRequest, backendPostWithoutRefresh } from '../../../../helpers/callBackend'
import ExpandMoreSharpIcon from '@mui/icons-material/ExpandMoreSharp'
import AddColumnButton from '../../../../components/specific/reports-custom/AddColumnButton'
import CustomReportColumn from '../../../../components/specific/reports-custom/CustomReportColumn'
import { useNavigate } from 'react-router-dom'

/**
 *
 * @param {*[]} array array of data to be changed
 * @param {number} idx0 index of data being moved
 * @param {number} idx1 index to swap data with
 * @returns {*[]} index swapped array
 */
const swapArrayItems = (array, idx0, idx1) => {
  const result = [...array]
  ;[result[idx0], result[idx1]] = [result[idx1], result[idx0]]
  return result
}

/**
 * Component state reducer (only called by using dispatch)
 * @param {Object} state Snapshot of current component state
 * @param {Object} action Object passed into dispatch(). Must contain a 'type' field
 * @returns {Object} Updated component state
 *
 * @example
 * Update group type and group multiselect options:
 * {
 *  type: 'GROUP_TYPE',
 *  payload: [string] // Group type code
 * }
 * Simple field update:
 * {
 *  type: 'SIMPLE_FIELD_UPDATE',
 *  payload: {
 *    [field]: [value] // field to update: value to set
 *  }
 * }
 * Add a column to the report
 * {
 *  type: 'ADD_COLUMN'
 * }
 * Remove a column from the report
 * {
 *  type: 'REMOVE_COLUMN',
 *  payload: [number] // column key to remove
 * }
 * Save column data from a column that was being edited
 * {
 *  type: 'SAVE_COLUMN',
 *  payload: {
 *    colKey: [number] // column key to save
 *    data: [Object] // column data from custom column component
 *  }
 * }
 * Shift a column +/- some number of indexes
 * {
 *  type: 'SHIFT_COLUMN':
 *  payload: {
 *    colKey: [number] // column key to save
 *    shift: [number] // positive or negative number to shift indexes
 *  }
 * }
 */
const reducer = (state, action) => {
  switch (action.type) {
    default:
      return state
    case 'GROUP_TYPE':
      return {
        ...state,
        groupType: action.payload,
        loadingOptions: true,
      }
    case 'SIMPLE_FIELD_UPDATE':
      return {
        ...state,
        ...action.payload,
      }
    case 'ADD_COLUMN':
      const newKey = state.lastColumnKey + 1
      return {
        ...state,
        lastColumnKey: newKey,
        columns: [
          ...state.columns,
          {
            colKey: newKey,
            id: undefined,
            header_name: undefined,
            column_type: undefined,
            end_date_adjustment: undefined,
            start_date_adjustement: undefined,
            start_date_adjustment_units: undefined,
          },
        ],
      }
    case 'REMOVE_COLUMN':
      const newCols = state.columns.filter((col) => col.colKey !== action.payload)
      return {
        ...state,
        columns: newCols,
      }
    case 'SAVE_COLUMN':
      const savedCols = state.columns.map((col) => {
        if (col.colKey === action.payload.colKey) {
          return { ...col, ...action.payload.data }
        }
        return col
      })
      return {
        ...state,
        columns: savedCols,
      }
    case 'SHIFT_COLUMN':
      const originalIdx = state.columns.findIndex((col) => col.colKey === action.payload.colKey)
      const newIdx = originalIdx + action.payload.shift

      if (newIdx < 0 || newIdx >= state.columns.length) {
        return {
          ...state,
        }
      }

      const shiftedCols = swapArrayItems(state.columns, originalIdx, newIdx)
      return {
        ...state,
        columns: shiftedCols,
      }
    case 'LOAD_REPORT':
      return {
        ...state,
        name: action.payload.name || '',
        groupType: action.payload.accountGroupingType || '',
        groupIds: action.payload.groupIds || [],
        columns: action.payload.columns || [],
        lastColumnKey: action.payload.lastColumnKey || 0,
        loadingOptions: true,
        loading: false,
      }
    case 'LOAD_GROUP_OPTIONS':
      return {
        ...state,
        groupOptions: action.payload || [],
        loadingOptions: false,
      }
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      }
  }
}

const getReportBuilder = async (dispatch, id) => {
  try {
    const reportResp = await backendGetRequest('/api/reports/v2', {
      params: {
        report_builder_id: id,
        serializer: 'columns',
      },
    })
    const reportBuilder = reportResp?.data?.data
    if (!reportBuilder) {
      throw new Error('Report builder not found')
    }
    const currentGroups = reportBuilder?.group_id?.split(',').map((idNum) => parseInt(idNum, 10))
    const lastKeyFromColumns = reportBuilder?.columns?.length || 0
    const keyedCols = reportBuilder.columns?.map((col, idx) => {
      return {
        ...col,
        colKey: idx,
      }
    })

    dispatch({
      type: 'LOAD_REPORT',
      payload: {
        name: reportBuilder.name || '',
        accountGroupingType: reportBuilder.account_grouping_type || '',
        groupIds: currentGroups,
        columns: keyedCols || [],
        lastColumnKey: lastKeyFromColumns,
      },
    })
  } catch (error) {
    errorMessage(error.message)
    dispatch({
      type: 'SET_LOADING',
      payload: false,
    })
  }
}

const getGroupTypeOptions = async (dispatch, groupType) => {
  if (!groupType) return

  try {
    const optionsResp = await backendGetRequest('/api/reports/v2/group-options', {
      params: {
        group_type: groupType,
      },
    })

    const options = optionsResp?.data?.data
    if (!options) {
      throw new Error('Group options not found')
    }

    dispatch({
      type: 'LOAD_GROUP_OPTIONS',
      payload: options,
    })
  } catch (error) {
    errorMessage(error.message)
    dispatch({
      type: 'LOAD_GROUP_OPTIONS',
      payload: [],
    })
  }
}

/**
 * Reused component styling
 */
const textFieldStyle = {
  size: 'small',
  variant: 'outlined',
  InputProps: {
    sx: {
      borderRadius: 0,
    },
  },
  sx: {
    backgroundColor: 'white',
    disableUnderline: true,
  },
  fullWidth: true,
}

const selectStyle = {
  size: 'small',
  IconComponent: ExpandMoreSharpIcon,
  sx: {
    borderRadius: 0,
    '&.Mui-disabled': {
      backgroundColor: theme.palette.secondary[100],
    },
  },
  fullWidth: true,
}

/**
 * Page component for editing or creating custom reports
 */
const AddOrEditReportPage = () => {
  const initialState = {
    name: null,
    groupType: null,
    groupOptions: [],
    groupIds: [],
    loadingOptions: false,
    loading: false,
    columns: [],
    lastColumnKey: 0,
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const { id } = useParams()
  const navigate = useNavigate()

  const handleGroupTypeChange = useCallback(
    (event) => {
      const {
        target: { value },
      } = event
      dispatch({
        type: 'GROUP_TYPE',
        payload: value,
      })
    },
    [dispatch]
  )

  const handleGroupSelectChange = useCallback(
    (event) => {
      const {
        target: { value },
      } = event
      dispatch({
        type: 'SIMPLE_FIELD_UPDATE',
        payload: {
          groupIds: typeof value === 'string' ? value.split(',') : value,
        },
      })
    },
    [dispatch]
  )

  const handleNameChange = useCallback(
    (event) => {
      const {
        target: { value },
      } = event
      dispatch({
        type: 'SIMPLE_FIELD_UPDATE',
        payload: {
          name: value,
        },
      })
    },
    [dispatch]
  )

  const handleSubmit = async () => {
    if (!state.name) {
      errorMessage('Please enter a report name.')
      return
    }
    if (!state.groupType) {
      errorMessage('Please select an account grouping type.')
      return
    }
    if (!state.groupIds.length) {
      errorMessage('Please select at least one accoount group')
      return
    }
    let allColumnsValid = true
    // A column object without a name indicates that at least one column isn't saved
    state.columns.every((col) => {
      if (!col.header_name) {
        allColumnsValid = false
        return false
      }
      return true
    })
    if (!allColumnsValid) {
      errorMessage('One or more columns not properly saved, please save or remove them')
      return
    }

    const data = {
      report_builder_id: id,
      name: state.name,
      group_type: state.groupType,
      group_ids: state.groupIds,
      columns: state.columns,
    }

    try {
      const reportResp = await backendPostWithoutRefresh('/api/reports/v2/', data)
      if (reportResp.status === 201) {
        navigate('/reports-custom')
      }
    } catch (error) {
      errorMessage(error.message)
    }
  }

  useEffect(() => {
    if (state.groupType) {
      getGroupTypeOptions(dispatch, state.groupType)
    }
  }, [state.groupType])

  useEffect(() => {
    if (id !== 'new' && !state.loading) {
      getReportBuilder(dispatch, id)
    }
  }, [id, state.loading])

  return (
    <Box sx={{ maxHeight: '99%', maxWidth: '99%', overflow: 'auto' }}>
      <Box
        sx={{
          margin: theme.dashboard.margins,
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button
            href='/reports-custom'
            disableElevation
            size='small'
            variant='outlined'
            sx={{ fontSize: 14, textTransform: 'none' }}
          >
            Back
          </Button>
          <HeaderBox title={`Custom Report ${id === 'new' ? 'Creator' : 'Editor'}`} />
        </Stack>
        <Box
          container
          sx={{
            width: 'inherit',
            margin: theme.spacing('10px', 0),
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.secondary.light}`,
            borderRadius: 1,
            padding: theme.spacing(1),
            overflow: 'auto',
          }}
        >
          <Grid container spacing={2} margin={theme.spacing(0)}>
            {/* First Row */}
            <Grid item md={1} sm={6} xs={12}>
              <Typography>Template Name</Typography>
            </Grid>
            <Grid item md={5.5} sm={6} xs={12}>
              <TextField value={state?.name || ''} onChange={handleNameChange} {...textFieldStyle} />
            </Grid>
            {/* null box to control grid spacing */}
            <Grid item md={5.5} sm={12} xs={12}>
              <Box display='none' />
            </Grid>

            {/* Second Row */}
            <Grid item md={1} sm={12} xs={12}>
              <Typography>Account Grouping</Typography>
            </Grid>
            <Grid item md={5.5} sm={6} xs={12}>
              <Select value={state?.groupType || ''} onChange={handleGroupTypeChange} {...selectStyle}>
                {groupingTypeFilter.map((item) => {
                  return (
                    <MenuItem value={item.value} key={item.label}>
                      {item.label}
                    </MenuItem>
                  )
                })}
              </Select>
            </Grid>
            <Grid item md={5.5} sm={6} xs={12}>
              <Select
                disabled={!state.groupType || state.loadingOptions}
                onChange={handleGroupSelectChange}
                multiple
                value={state.groupIds}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, overflow: 'auto', maxHeight: '60px' }}>
                    {selected.map((value) => {
                      const menuItem = state.groupOptions.find((item) => item.id === value)
                      return menuItem ? (
                        <Chip
                          key={value}
                          sx={{ borderRadius: 0, height: '20px' }}
                          label={<Typography variant='body2'>{menuItem.name}</Typography>}
                        />
                      ) : null
                    })}
                  </Box>
                )}
                {...selectStyle}
              >
                {state.groupOptions.map((option) => (
                  <MenuItem
                    value={option.id}
                    key={option.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 16px',
                    }}
                  >
                    <ListItemText primary={option.name} />
                    {state.groupIds.includes(option.id) && <CheckIcon />}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Stack direction='row' spacing={1} sx={{ margin: theme.spacing(0) }}>
            {state?.columns?.map((column) => {
              return (
                <CustomReportColumn
                  key={column.colKey}
                  columnData={column}
                  removeColumn={() => {
                    dispatch({
                      type: 'REMOVE_COLUMN',
                      payload: column.colKey,
                    })
                  }}
                  saveColumn={(data) => {
                    dispatch({
                      type: 'SAVE_COLUMN',
                      payload: {
                        colKey: column.colKey,
                        data: data,
                      },
                    })
                  }}
                  shitfLeft={() => {
                    dispatch({
                      type: 'SHIFT_COLUMN',
                      payload: {
                        colKey: column.colKey,
                        shift: -1,
                      },
                    })
                  }}
                  shiftRight={() => {
                    dispatch({
                      type: 'SHIFT_COLUMN',
                      payload: {
                        colKey: column.colKey,
                        shift: 1,
                      },
                    })
                  }}
                />
              )
            })}
            <AddColumnButton
              onClick={() => {
                dispatch({
                  type: 'ADD_COLUMN',
                })
              }}
            />
          </Stack>
          <Button
            disableElevation
            size='small'
            variant='contained'
            sx={{ fontSize: 14, textTransform: 'none', marginTop: '10px' }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default AddOrEditReportPage
